import { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import HomeHeader from "../../components/header/HomeHeader";
import "./Home.css";
import { appApiHandler } from "../../api/appApiHandler";
import { getToken, getUserId } from "../../constants/asyncStoreManager";
import Banner from "./components/Banner";
import TitleHeader from "./components/TitleHeader";
import ProductCard from "./components/ProductCard";
import ShopByCategory from "./components/ShopByCategory";
import ShopByReels from "./components/ShopByReels";
import { setCategories } from "../../store/slices/categoryState";
import CommonFooter from "../../components/footer/CommonFooter";
import { setCartCount, setWhishlistCount } from "../../store/slices/countState";
import { setWhishlist } from "../../store/slices/whishlistState";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { colors } from "../../constants/colors";
import CommonButton from "../../components/button/CommonButton";
import { setNewMessage } from "../../store/slices/supportTicketState";

const Home = () => {
  const dispatch = useDispatch();
  const { categories } = useSelector((state) => state.categoryState);

  const bannerListRef = useRef(null);

  const [banners, setBanners] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newArrivalsProducts, setNewArrivalsProducts] = useState([]);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);

  // Featured products pagination state
  const [featuredPage, setFeaturedPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalFeaturedCount, setTotalFeaturedCount] = useState(0);

  // New arrivals products pagination state
  const [newArrivalsPage, setNewArrivalsPage] = useState(1);
  const [loadingMoreNewArrivals, setLoadingMoreNewArrivals] = useState(false);
  const [totalNewArrivalsCount, setTotalNewArrivalsCount] = useState(0);

  // Trending products pagination state
  const [trendingPage, setTrendingPage] = useState(1);
  const [loadingMoreTrending, setLoadingMoreTrending] = useState(false);
  const [totalTrendingCount, setTotalTrendingCount] = useState(0);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Update current index when user scrolls manually
  useEffect(() => {
    const scroller = bannerListRef.current;
    if (!scroller) return;

    const handleScroll = () => {
      const currentIndex = Math.round(scroller.scrollLeft / windowWidth);
      setCurrentBannerIndex(currentIndex);
    };

    scroller.addEventListener('scroll', handleScroll);
    return () => scroller.removeEventListener('scroll', handleScroll);
  }, [windowWidth]);

  const BANNER_WIDTH = useMemo(() => {
    return Math.max(0, windowWidth);
  }, [windowWidth]);

  // Calculate alternating background colors for visible sections
  // Pattern: no color (0) → white (1) → no color (0) → white (1) → ...
  // Only sections with data are counted
  const sectionBackgrounds = useMemo(() => {
    const sections = [
      { key: 'categories', hasData: categories?.length > 0 },
      { key: 'featured', hasData: featuredProducts?.length > 0 },
      { key: 'newArrivals', hasData: newArrivalsProducts?.length > 0 },
      { key: 'reels', hasData: videos?.length > 0 },
      { key: 'trending', hasData: trendingProducts?.length > 0 },
    ];

    const result = {};
    let visibleIndex = 0;

    sections.forEach((section) => {
      if (section.hasData) {
        // Even index = no color (false), Odd index = white (true)
        result[section.key] = visibleIndex % 2 === 1;
        visibleIndex++;
      } else {
        result[section.key] = false;
      }
    });

    return result;
  }, [categories?.length, featuredProducts?.length, newArrivalsProducts?.length, videos?.length, trendingProducts?.length]);

  const getHomeData = async () => {
    const reqData = {
      limit: 4
    }
    const response = await appApiHandler.getHomeData(reqData);
    const featuredReqData = {
      category_id: "featured",
      page: 1,
      limit: 8
    }
    const featuredProducts = await appApiHandler.getProducts(featuredReqData);
    if (featuredProducts?.status === 200 && featuredProducts?.data) {
      setFeaturedProducts(featuredProducts?.data || []);
      setTotalFeaturedCount(featuredProducts?.total_count || 0);
    }

    // Get new arrivals products
    const newArrivalsReqData = {
      category_id: "new_arrivals",
      page: 1,
      limit: 8
    }
    const newArrivalsProducts = await appApiHandler.getProducts(newArrivalsReqData);
    if (newArrivalsProducts?.status === 200 && newArrivalsProducts?.data) {
      setNewArrivalsProducts(newArrivalsProducts?.data || []);
      setTotalNewArrivalsCount(newArrivalsProducts?.total_count || 0);
    }

    // Get trending products (without category_id)
    const trendingReqData = {
      page: 1,
      limit: 8
    }
    const trendingProductsResponse = await appApiHandler.getProducts(trendingReqData);
    if (trendingProductsResponse?.status === 200 && trendingProductsResponse?.data) {
      setTrendingProducts(trendingProductsResponse?.data || []);
      setTotalTrendingCount(trendingProductsResponse?.total_count || 0);
    }

    if (response?.status === 200) {
      setBanners(response?.banners || []);
      setVideos(response?.videos || []);
    }
    const categories = await appApiHandler.getCategories()
    if (categories?.status === 200 && categories?.data) {
      const filteredCategories = categories.data.filter((cat) => cat.active_product_count > 0);
      dispatch(setCategories(filteredCategories))
    }
    const isLoggedIn = await getToken();
    if (isLoggedIn) {
      const user_id = await getUserId();
      const reqData = {
        user_id: user_id
      }
      const whishlistResponse = await appApiHandler.getWishlist(reqData);
      if (whishlistResponse?.status === 200) {
        dispatch(setWhishlist(whishlistResponse?.data || []));
        dispatch(setWhishlistCount(whishlistResponse?.data?.length));
      }
      const cartResponse = await appApiHandler.getCart(reqData);
      if (cartResponse?.status === 200 && cartResponse?.data?.products) {
        dispatch(setCartCount(cartResponse?.data?.products?.length));
      }

      const ticketResponse = await appApiHandler.getSupportTickets(reqData);
      if (ticketResponse?.status === 200 && ticketResponse?.data) {
        const hasUnread = Array.isArray(ticketResponse?.data) && ticketResponse.data.some((t) => Number(t?.unread_count) > 0);
        dispatch(setNewMessage(hasUnread ? 1 : 0));
      }

    }
    setLoading(false);
  }

  useEffect(() => {
    setLoading(true);
    getHomeData();
  }, [])

  // Auto-advance banner with scrollTo
  useEffect(() => {
    if (!bannerListRef.current || banners.length === 0 || windowWidth === 0) return;
    const scroller = bannerListRef.current;
    const intervalId = setInterval(() => {
      const currentIndex = Math.round(scroller.scrollLeft / windowWidth);
      const nextIndex = (currentIndex + 1) % banners.length;
      scroller.scrollTo({ left: nextIndex * windowWidth, behavior: "smooth" });
      setCurrentBannerIndex(nextIndex);
    }, 5000);
    return () => clearInterval(intervalId);
  }, [banners, windowWidth]);

  // Navigation functions with scrollTo
  const goToNextBanner = () => {
    if (!bannerListRef.current || banners.length === 0) return;
    const scroller = bannerListRef.current;
    const currentIndex = Math.round(scroller.scrollLeft / windowWidth);
    const nextIndex = (currentIndex + 1) % banners.length;
    scroller.scrollTo({ left: nextIndex * windowWidth, behavior: "smooth" });
    setCurrentBannerIndex(nextIndex);
  };

  const goToPreviousBanner = () => {
    if (!bannerListRef.current || banners.length === 0) return;
    const scroller = bannerListRef.current;
    const currentIndex = Math.round(scroller.scrollLeft / windowWidth);
    const prevIndex = currentIndex === 0 ? banners.length - 1 : currentIndex - 1;
    scroller.scrollTo({ left: prevIndex * windowWidth, behavior: "smooth" });
    setCurrentBannerIndex(prevIndex);
  };

  const goToBanner = (index) => {
    if (!bannerListRef.current || banners.length === 0) return;
    const scroller = bannerListRef.current;
    scroller.scrollTo({ left: index * windowWidth, behavior: "smooth" });
    setCurrentBannerIndex(index);
  };

  // Load more featured products
  const loadMoreFeaturedProducts = async () => {
    if (loadingMore || featuredProducts.length >= totalFeaturedCount) return;

    setLoadingMore(true);
    const nextPage = featuredPage + 1;
    const featuredReqData = {
      category_id: "featured",
      page: nextPage,
      limit: 8
    };

    try {
      const response = await appApiHandler.getProducts(featuredReqData);
      if (response?.status === 200 && response?.data) {
        setFeaturedProducts(prev => [...prev, ...response.data]);
        setFeaturedPage(nextPage);
      }
    } catch (error) {
      console.error('Error loading more featured products:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Load more new arrivals products
  const loadMoreNewArrivalsProducts = async () => {
    if (loadingMoreNewArrivals || newArrivalsProducts.length >= totalNewArrivalsCount) return;

    setLoadingMoreNewArrivals(true);
    const nextPage = newArrivalsPage + 1;
    const newArrivalsReqData = {
      category_id: "new_arrivals",
      page: nextPage,
      limit: 8
    };

    try {
      const response = await appApiHandler.getProducts(newArrivalsReqData);
      if (response?.status === 200 && response?.data) {
        setNewArrivalsProducts(prev => [...prev, ...response.data]);
        setNewArrivalsPage(nextPage);
      }
    } catch (error) {
      console.error('Error loading more new arrivals products:', error);
    } finally {
      setLoadingMoreNewArrivals(false);
    }
  };

  // Load more trending products
  const loadMoreTrendingProducts = async () => {
    if (loadingMoreTrending || trendingProducts.length >= totalTrendingCount) return;

    setLoadingMoreTrending(true);
    const nextPage = trendingPage + 1;
    const trendingReqData = {
      page: nextPage,
      limit: 8
    };

    try {
      const response = await appApiHandler.getProducts(trendingReqData);
      if (response?.status === 200 && response?.data) {
        setTrendingProducts(prev => [...prev, ...response.data]);
        setTrendingPage(nextPage);
      }
    } catch (error) {
      console.error('Error loading more trending products:', error);
    } finally {
      setLoadingMoreTrending(false);
    }
  };

  return (
    <div className="home-container">
      <HomeHeader />
      <main className="home-main">

        {banners?.length > 0 && (
          <section className="home-banners">
            <div className="home-banners__container">
              {/* Scrollable Banner Container */}
              <div
                className="home-banners__scroller"
                ref={bannerListRef}
                style={{ gridAutoColumns: `${windowWidth}px` }}
              >
                {banners.map((item, index) => (
                  <div key={item?._id} className="home-banners__item">
                    <Banner item={item} />
                  </div>
                ))}
              </div>

              {/* Navigation Arrows */}
              {banners.length > 1 && (
                <>
                  <button
                    className="home-banners__nav home-banners__nav--prev"
                    onClick={goToPreviousBanner}
                  >
                    <FaChevronLeft color={colors.primary} />
                  </button>
                  <button
                    className="home-banners__nav home-banners__nav--next"
                    onClick={goToNextBanner}
                  >
                    <FaChevronRight color={colors.primary} />
                  </button>
                </>
              )}

              {/* Dots Navigation */}
              {banners.length > 1 && (
                <div className="home-banners__dots">
                  {banners.map((_, index) => (
                    <button
                      key={index}
                      className={`home-banners__dot ${index === currentBannerIndex ? 'home-banners__dot--active' : ''
                        }`}
                      onClick={() => goToBanner(index)}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {
          categories?.length > 0 && (
            <ShopByCategory categories={categories} hasWhiteBg={sectionBackgrounds.categories} />
          )
        }


        {featuredProducts?.length > 0 && (
          <section className={`home-featured ${sectionBackgrounds.featured ? 'home-featured--white' : ''}`}>
            <TitleHeader
              title={"Featured Products"}
              _id={"featured"}
              subtitle='Hot Selling'
            />
            <div className="productGrid">
              {featuredProducts.map((item) => (
                <ProductCard
                  key={item?._id}
                  item={item}
                />
              ))}
            </div>

            {/* Load More Section */}
            {featuredProducts.length < totalFeaturedCount && (
              <div className="load-more-section">
                <div className="load-more-progress">
                  <p className="load-more-text">
                    You've viewed {featuredProducts.length} of {totalFeaturedCount} products
                  </p>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${(featuredProducts.length / totalFeaturedCount) * 100}%` }}
                    ></div>
                  </div>
                </div>
                <CommonButton
                  title="Load More"
                  onPress={loadMoreFeaturedProducts}
                  disabled={loadingMore}
                  isLoading={loadingMore}
                  customClassName="load-more-button"
                  outlined
                />
              </div>
            )}
          </section>
        )}

        {newArrivalsProducts?.length > 0 && (
          <section
            className={`home-new-arrivals ${sectionBackgrounds.newArrivals ? 'home-new-arrivals--white' : ''}`}
          >
            <TitleHeader
              title={"New Arrivals"}
              _id={"new_arrivals"}
              subtitle=''
            />
            <div className="productGrid">
              {newArrivalsProducts.map((item) => (
                <ProductCard
                  key={item?._id}
                  item={item}
                />
              ))}
            </div>

            {/* Load More Section */}
            {newArrivalsProducts.length < totalNewArrivalsCount && (
              <div className="load-more-section">
                <div className="load-more-progress">
                  <p className="load-more-text">
                    You've viewed {newArrivalsProducts.length} of {totalNewArrivalsCount} products
                  </p>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${(newArrivalsProducts.length / totalNewArrivalsCount) * 100}%` }}
                    ></div>
                  </div>
                </div>
                <CommonButton
                  title="Load More"
                  onPress={loadMoreNewArrivalsProducts}
                  disabled={loadingMoreNewArrivals}
                  isLoading={loadingMoreNewArrivals}
                  customClassName="load-more-button"
                  outlined
                />
              </div>
            )}
          </section>
        )}

        {
          videos?.length > 0 && (
            <ShopByReels videos={videos} hasWhiteBg={sectionBackgrounds.reels} />
          )
        }

        {trendingProducts?.length > 0 && (
          <section className={`home-trending ${sectionBackgrounds.trending ? 'home-trending--white' : ''}`}>
            <TitleHeader
              title={"Trending"}
              _id={"trending"}
              subtitle='Top view in this week'
            />
            <div className="productGrid">
              {trendingProducts.map((item) => (
                <ProductCard
                  key={item?._id}
                  item={item}
                />
              ))}
            </div>

            {/* Load More Section */}
            {trendingProducts.length < totalTrendingCount && (
              <div className="load-more-section">
                <div className="load-more-progress">
                  <p className="load-more-text">
                    You've viewed {trendingProducts.length} of {totalTrendingCount} products
                  </p>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${(trendingProducts.length / totalTrendingCount) * 100}%` }}
                    ></div>
                  </div>
                </div>
                <CommonButton
                  title="Load More"
                  onPress={loadMoreTrendingProducts}
                  disabled={loadingMoreTrending}
                  isLoading={loadingMoreTrending}
                  customClassName="load-more-button"
                  outlined
                />
              </div>
            )}
          </section>
        )}

      </main>
      <CommonFooter />
    </div>
  );
};

export default Home;
